import React, { useState, useEffect } from 'react'
import {
  Layout,
  Table,
  Button,
  Space,
  Tag,
  Typography,
  message,
  Modal,
  Select,
  Input,
  Checkbox,
  Dropdown,
  Card,
  Row,
  Col,
  Statistic,
  Tooltip,
  Popconfirm,
  Badge,
  Breadcrumb
} from 'antd'
import {
  DeleteOutlined,
  TagOutlined,
  FolderOutlined,
  InboxOutlined,
  EyeOutlined,
  EditOutlined,
  MoreOutlined,
  SelectOutlined,
  ClearOutlined,
  ExportOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  LinkOutlined,
  HomeOutlined,
  ArrowLeftOutlined,
  CheckOutlined,
  StarOutlined
} from '@ant-design/icons'
import { useNavigate, useSearchParams } from 'react-router-dom'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import 'dayjs/locale/zh-cn'
import { api } from '../services/api'
import './Articles.css'

dayjs.extend(relativeTime)
dayjs.locale('zh-cn')

const { Content } = Layout
const { Title, Text } = Typography
const { Option } = Select

interface Article {
  id: number
  title: string
  url: string
  summary: string
  author?: string
  site_name?: string
  collected_at: string
  reading_time: number
  word_count: number
  tags: Array<{ id: number; name: string; color?: string }>
  folder?: { id: number; name: string }
  is_archived: boolean
  is_read: boolean
  is_featured: boolean
}

interface Tag {
  id: number
  name: string
  color?: string
}

interface Folder {
  id: number
  name: string
}

interface BatchOperationModalProps {
  visible: boolean
  onCancel: () => void
  onConfirm: (operation: string, data: any) => void
  selectedCount: number
  operation: 'tag' | 'folder' | 'archive' | 'read' | 'delete' | 'featured' | null
  tags: Tag[]
  folders: Folder[]
}

const BatchOperationModal: React.FC<BatchOperationModalProps> = ({
  visible,
  onCancel,
  onConfirm,
  selectedCount,
  operation,
  tags,
  folders
}) => {
  const [selectedTags, setSelectedTags] = useState<number[]>([])
  const [selectedTagNames, setSelectedTagNames] = useState<string[]>([])
  const [selectedFolder, setSelectedFolder] = useState<number | null>(null)

  const handleConfirm = () => {
    switch (operation) {
      case 'tag':
        if (selectedTagNames.length === 0) {
          message.warning('请选择要添加的标签')
          return
        }
        onConfirm('tag', { tag_names: selectedTagNames })
        break
      case 'folder':
        if (!selectedFolder) {
          message.warning('请选择要移动到的文件夹')
          return
        }
        onConfirm('folder', { folder_id: selectedFolder })
        break
      case 'archive':
        onConfirm('archive', {})
        break
      case 'read':
        onConfirm('read', {})
        break
      case 'featured':
        onConfirm('featured', {})
        break
      case 'delete':
        onConfirm('delete', {})
        break
    }
  }

  const getTitle = () => {
    switch (operation) {
      case 'tag': return '批量添加标签'
      case 'folder': return '批量移动到文件夹'
      case 'archive': return '批量归档'
      case 'read': return '批量标记已读'
      case 'featured': return '批量精选/取消精选'
      case 'delete': return '批量删除'
      default: return '批量操作'
    }
  }

  const getContent = () => {
    switch (operation) {
      case 'tag':
        return (
          <div>
            <p>为选中的 {selectedCount} 篇文章添加标签：</p>
            <Select
              mode="tags"
              style={{ width: '100%' }}
              placeholder="选择或输入标签名称，按回车添加"
              value={selectedTagNames}
              onChange={setSelectedTagNames}
              tokenSeparators={[',']}
            >
              {tags.map(tag => (
                <Option key={tag.name} value={tag.name}>
                  <Tag color={tag.color}>{tag.name}</Tag>
                </Option>
              ))}
            </Select>
            <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
              提示：可以输入新标签名称并按回车键添加，系统会自动创建不存在的标签
            </div>
          </div>
        )
      case 'folder':
        return (
          <div>
            <p>将选中的 {selectedCount} 篇文章移动到文件夹：</p>
            <Select
              style={{ width: '100%' }}
              placeholder="选择文件夹"
              value={selectedFolder}
              onChange={setSelectedFolder}
            >
              {folders.map(folder => (
                <Option key={folder.id} value={folder.id}>
                  <FolderOutlined /> {folder.name}
                </Option>
              ))}
            </Select>
          </div>
        )
      case 'archive':
        return (
          <p>确定要归档选中的 {selectedCount} 篇文章吗？归档后的文章将不会在主列表中显示。</p>
        )
      case 'read':
        return (
          <p>确定要将选中的 {selectedCount} 篇文章标记为已读吗？</p>
        )
      case 'featured':
        return (
          <p>确定要将选中的 {selectedCount} 篇文章设置为精选吗？</p>
        )
      case 'delete':
        return (
          <p style={{ color: '#ff4d4f' }}>
            确定要删除选中的 {selectedCount} 篇文章吗？此操作不可撤销！
          </p>
        )
      default:
        return null
    }
  }

  return (
    <Modal
      title={getTitle()}
      open={visible}
      onCancel={onCancel}
      onOk={handleConfirm}
      okText="确认"
      cancelText="取消"
      okButtonProps={{
        danger: operation === 'delete'
      }}
    >
      {getContent()}
    </Modal>
  )
}

const Articles: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [articles, setArticles] = useState<Article[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [tags, setTags] = useState<Tag[]>([])
  const [folders, setFolders] = useState<Folder[]>([])
  const [batchModalVisible, setBatchModalVisible] = useState(false)
  const [batchOperation, setBatchOperation] = useState<'tag' | 'folder' | 'archive' | 'read' | 'delete' | 'featured' | null>(null)
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  })
  const [filters, setFilters] = useState({
    is_archived: false,
    is_read: null as boolean | null,
    is_featured: null as boolean | null,
    tag_id: null as number | null,
    folder_id: null as number | null
  })
  const [weeklyNewCount, setWeeklyNewCount] = useState(0)

  // 从URL参数初始化分页和过滤条件（用于从详情页返回时恢复状态）
  useEffect(() => {
    const page = searchParams.get('page')
    const archived = searchParams.get('archived')
    const read = searchParams.get('read')
    const unread = searchParams.get('unread')
    const featured = searchParams.get('featured')
    const tag_id = searchParams.get('tag_id')
    const folder_id = searchParams.get('folder_id')

    const filterUpdates: any = {}
    if (archived) filterUpdates.is_archived = archived === '1'
    if (read) filterUpdates.is_read = true
    else if (unread) filterUpdates.is_read = false
    if (featured) filterUpdates.is_featured = featured === '1'
    if (tag_id) filterUpdates.tag_id = parseInt(tag_id)
    if (folder_id) filterUpdates.folder_id = parseInt(folder_id)

    if (Object.keys(filterUpdates).length > 0) {
      setFilters(prev => ({ ...prev, ...filterUpdates }))
    }

    if (page) {
      const p = parseInt(page)
      if (!isNaN(p) && p > 1) {
        setPagination(prev => ({ ...prev, current: p }))
      }
    }
  }, [searchParams])

  useEffect(() => {
    loadArticles()
    loadTags()
    loadFolders()
  }, [pagination.current, pagination.pageSize, filters])

  // 当文章数据更新时，重新计算本周新增数量
  useEffect(() => {
    calculateWeeklyNewCount()
  }, [articles])

  const loadArticles = async () => {
    try {
      setLoading(true)
      const params: any = {
        page: pagination.current,
        page_size: pagination.pageSize,
        sort_by: 'collected_at',
        order: 'desc'
      }

      // 添加筛选条件
      if (filters.is_archived !== null) {
        params.is_archived = filters.is_archived
      }
      if (filters.is_read !== null) {
        params.is_read = filters.is_read
      }
      if (filters.is_featured !== null) {
        params.is_featured = filters.is_featured
      }
      if (filters.tag_id) {
        params.tag_id = filters.tag_id
      }
      if (filters.folder_id) {
        params.folder_id = filters.folder_id
      }

      const response = await api.articles.list(params)
      if (response.success) {
        setArticles(response.data.items || [])
        setPagination(prev => ({
          ...prev,
          total: response.data.total || 0
        }))
      }
    } catch (error) {
      console.error('加载文章列表失败:', error)
      message.error('加载文章列表失败')
    } finally {
      setLoading(false)
    }
  }

  // 计算本周新增数量
  const calculateWeeklyNewCount = () => {
    const now = dayjs()
    const startOfWeek = now.startOf('week') // 周一0点

    const weeklyCount = articles.filter(article => {
      const collectedAt = dayjs(article.collected_at)
      return collectedAt.isAfter(startOfWeek)
    }).length

    setWeeklyNewCount(weeklyCount)
  }

  const loadTags = async () => {
    try {
      const response = await api.tags.list()
      if (response.success) {
        setTags(response.data || [])
      }
    } catch (error) {
      console.error('加载标签失败:', error)
    }
  }

  const loadFolders = async () => {
    try {
      const response = await api.folders.list()
      if (response.success) {
        setFolders(response.data || [])
      }
    } catch (error) {
      console.error('加载文件夹失败:', error)
    }
  }

  const handleBatchOperation = async (operation: string, data: any) => {
    try {
      const articleIds = selectedRowKeys as number[]

      switch (operation) {
        case 'tag':
          await api.articles.batchAddTagsByNames(articleIds, data.tag_names)
          message.success(`成功为 ${articleIds.length} 篇文章添加标签`)
          break
        case 'folder':
          await api.articles.batchMoveToFolder(articleIds, data.folder_id)
          message.success(`成功将 ${articleIds.length} 篇文章移动到文件夹`)
          break
        case 'archive':
          await api.articles.batchArchive(articleIds)
          message.success(`成功归档 ${articleIds.length} 篇文章`)
          break
        case 'read':
          await api.articles.batchSetRead(articleIds)
          message.success(`成功标记 ${articleIds.length} 篇文章为已读`)
          break
        case 'featured':
          await api.articles.batchSetFeatured(articleIds)
          message.success(`成功将 ${articleIds.length} 篇文章设置为精选`)
          break
        case 'delete':
          await api.articles.batchDelete(articleIds)
          message.success(`成功删除 ${articleIds.length} 篇文章`)
          break
      }

      setBatchModalVisible(false)
      setBatchOperation(null)
      setSelectedRowKeys([])
      loadArticles()
    } catch (error) {
      console.error('批量操作失败:', error)
      message.error('批量操作失败')
    }
  }

  const handleTableChange = (paginationConfig: any) => {
    setPagination({
      ...pagination,
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize
    })
  }

  const handleRowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(newSelectedRowKeys)
    },
    onSelectAll: (selected: boolean, selectedRows: Article[], changeRows: Article[]) => {
      console.log('全选状态:', selected, '选中行:', selectedRows.length, '变更行:', changeRows.length)
    },
    onSelect: (record: Article, selected: boolean, selectedRows: Article[]) => {
      console.log('选择行:', record.id, '选中状态:', selected, '当前选中:', selectedRows.length)
    }
  }

  const columns = [
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      width: '25%',
      render: (title: string, record: Article) => (
        <div>
          <div style={{ marginBottom: 2 }}>
            <Button
              type="link"
              style={{ padding: 0, height: 'auto', fontWeight: 500, fontSize: 13 }}
              onClick={() => {
                // 保存当前分页和筛选状态，以便返回时恢复
                sessionStorage.setItem('articles_list_state', JSON.stringify({
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                  filters: filters
                }))
                navigate(`/articles/${record.id}`)
              }}
              title={title}
            >
              {(() => {
                // 最保守的截断策略：只有非常长的标题才截断
                if (title.length <= 80) return title;

                // 移除可能的"...."干扰标记
                let cleanTitle = title.replace(/^\.{4}/, '').trim();

                // 如果清理后还是很长，进行智能截断
                if (cleanTitle.length <= 80) return cleanTitle;

                // 查找" - "分隔符，优先保留平台信息
                const separatorIndex = cleanTitle.lastIndexOf(' - ');

                if (separatorIndex > 0 && separatorIndex < cleanTitle.length - 5) {
                  // 有分隔符：保留前60字符 + 完整后缀
                  const prefix = cleanTitle.substring(0, 60);
                  const suffix = cleanTitle.substring(separatorIndex); // 包含" - "和后面
                  // 确保不会重复添加省略号
                  if (!prefix.endsWith('...') && !suffix.startsWith('...')) {
                    return `${prefix}...${suffix}`;
                  } else {
                    return `${prefix}${suffix}`;
                  }
                } else {
                  // 无分隔符：保留前60字符 + "..." + 后20字符
                  const prefix = cleanTitle.substring(0, 60);
                  const suffix = cleanTitle.substring(cleanTitle.length - 20);
                  return `${prefix}...${suffix}`;
                }
              })()}
            </Button>
          </div>
          <div>
            {record.is_featured && (
              <Tag color="gold" style={{ marginRight: 4, fontSize: 10 }}>
                ⭐ 精选
              </Tag>
            )}
          </div>
          <div>
            {record.is_read && (
              <Tag color="green" style={{ marginRight: 4, fontSize: 10 }}>
                已读
              </Tag>
            )}
            {record.is_archived && (
              <Tag color="orange" style={{ fontSize: 10 }}>
                已归档
              </Tag>
            )}
          </div>
        </div>
      )
    },
    {
      title: '来源',
      dataIndex: 'site_name',
      key: 'site_name',
      width: '12%',
      render: (siteName: string, record: Article) => (
        <div>
          <div style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={siteName}>
            {siteName || '未知来源'}
          </div>
          {record.author && (
            <Text type="secondary" style={{ fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={record.author}>
              {record.author}
            </Text>
          )}
        </div>
      )
    },
    {
      title: '标签',
      dataIndex: 'tags',
      key: 'tags',
      width: '16%',
      render: (tags: Tag[]) => (
        <div>
          {tags?.slice(0, 2).map(tag => (
            <Tag key={tag.id} color={tag.color || 'blue'} style={{ marginBottom: 1, fontSize: 10 }}>
              {tag.name.length > 6 ? `${tag.name.substring(0, 6)}...` : tag.name}
            </Tag>
          ))}
          {tags?.length > 2 && (
            <Tag style={{ marginBottom: 1, fontSize: 10 }}>+{tags.length - 2}</Tag>
          )}
        </div>
      )
    },
    {
      title: '文件夹',
      dataIndex: 'folder',
      key: 'folder',
      width: '10%',
      render: (folder: Folder) => (
        folder ? (
          <Tag icon={<FolderOutlined />} color="orange" style={{ fontSize: 11 }}>
            {folder.name.length > 6 ? `${folder.name.substring(0, 6)}...` : folder.name}
          </Tag>
        ) : (
          <Text type="secondary" style={{ fontSize: 11 }}>未分类</Text>
        )
      )
    },
    {
      title: '统计',
      key: 'stats',
      width: '10%',
      render: (_, record: Article) => (
        <div>
          <div style={{ fontSize: 11, color: '#666' }}>
            <ClockCircleOutlined /> {record.reading_time}分
          </div>
          <div style={{ fontSize: 11, color: '#666' }}>
            <FileTextOutlined /> {record.word_count}字
          </div>
        </div>
      )
    },
    {
      title: '收藏时间',
      dataIndex: 'collected_at',
      key: 'collected_at',
      width: '10%',
      render: (date: string) => (
        <div>
          <div style={{ fontSize: 12 }}>
            {dayjs(date).format('MM-DD')}
          </div>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {dayjs(date).fromNow()}
          </Text>
        </div>
      )
    },
    {
      title: '操作',
      key: 'actions',
      width: '10%',
      render: (_, record: Article) => (
        <Space size="small">
          <Tooltip title="查看原文">
            <Button
              type="text"
              size="small"
              icon={<LinkOutlined />}
              onClick={() => window.open(record.url, '_blank')}
            />
          </Tooltip>
          <Tooltip title="阅读详情">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => {
                // 保存当前分页和筛选状态，以便返回时恢复
                sessionStorage.setItem('articles_list_state', JSON.stringify({
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                  filters: filters
                }))
                navigate(`/articles/${record.id}`)
              }}
            />
          </Tooltip>
<Dropdown
            menu={{
              items: [
                {
                  key: 'edit',
                  label: '编辑',
                  icon: <EditOutlined />,
                  onClick: () => navigate(`/articles/${record.id}`)
                },
                {
                  key: 'archive',
                  label: record.is_archived ? '取消归档' : '归档',
                  icon: <InboxOutlined />,
                  onClick: async () => {
                    try {
                      if (record.is_archived) {
                        await api.articles.batchUnarchive([record.id])
                        message.success('已取消归档')
                      } else {
                        await api.articles.batchArchive([record.id])
                        message.success('已归档')
                      }
                      loadArticles()
                    } catch (error) {
                      console.error('归档操作失败:', error)
                      message.error('操作失败')
                    }
                  }
                },
                {
                  type: 'divider'
                },
                {
                  key: 'delete',
                  label: '删除',
                  icon: <DeleteOutlined />,
                  danger: true,
                  onClick: () => {
                    Modal.confirm({
                      title: '确认删除',
                      content: `确定要删除文章「${record.title.substring(0, 20)}${record.title.length > 20 ? '...' : ''}」吗？此操作不可撤销。`,
                      okText: '删除',
                      cancelText: '取消',
                      okButtonProps: { danger: true },
                      onOk: async () => {
                        try {
                          await api.articles.delete(record.id)
                          message.success('删除成功')
                          loadArticles()
                        } catch (error) {
                          console.error('删除失败:', error)
                          message.error('删除失败')
                        }
                      }
                    })
                  }
                }
              ]
            }}
            trigger={['click']}
          >
            <Button type="text" size="small" icon={<MoreOutlined />} />
          </Dropdown>
        </Space>
      )
    }
  ]

  const batchOperationItems = [
    {
      key: 'tag',
      label: '添加标签',
      icon: <TagOutlined />,
      onClick: () => {
        setBatchOperation('tag')
        setBatchModalVisible(true)
      }
    },
    {
      key: 'folder',
      label: '移动到文件夹',
      icon: <FolderOutlined />,
      onClick: () => {
        setBatchOperation('folder')
        setBatchModalVisible(true)
      }
    },
    {
      key: 'archive',
      label: '批量归档',
      icon: <InboxOutlined />,
      onClick: () => {
        setBatchOperation('archive')
        setBatchModalVisible(true)
      }
    },
    {
      key: 'read',
      label: '批量标记已读',
      icon: <CheckOutlined />,
      onClick: () => {
        setBatchOperation('read')
        setBatchModalVisible(true)
      }
    },
    {
      key: 'featured',
      label: '批量精选',
      icon: <StarOutlined />,
      onClick: () => {
        setBatchOperation('featured')
        setBatchModalVisible(true)
      }
    },
    {
      type: 'divider' as const
    },
    {
      key: 'delete',
      label: '批量删除',
      icon: <DeleteOutlined />,
      danger: true,
      onClick: () => {
        setBatchOperation('delete')
        setBatchModalVisible(true)
      }
    }
  ]

  return (
    <Layout style={{ minHeight: '100vh', background: '#f5f5f5' }}>
      <Content style={{ padding: '24px' }}>
        {/* 页面导航和标题 */}
        <div style={{ marginBottom: 16 }}>
          {/* 面包屑导航和标题行 */}
          <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
            <Col xs={24} sm={24} md={12}>
              <Space>
                <Button
                  icon={<ArrowLeftOutlined />}
                  onClick={() => navigate('/')}
                  type="text"
                  size="small"
                >
                  返回首页
                </Button>
                <Breadcrumb
                  items={[
                    {
                      title: (
                        <Button
                          type="link"
                          icon={<HomeOutlined />}
                          onClick={() => navigate('/')}
                          style={{ padding: 0 }}
                          size="small"
                        >
                          首页
                        </Button>
                      )
                    },
                    { title: '文章管理' }
                  ]}
                />
              </Space>
            </Col>
            <Col xs={24} sm={24} md={12}>
              <Title level={3} style={{ margin: 0, textAlign: 'right' }}>
                <FileTextOutlined style={{ marginRight: 8 }} />
                文章管理
              </Title>
            </Col>
          </Row>

          {/* 统计卡片行 */}
          <Row gutter={[8, 8]}>
            <Col xs={12} sm={6} md={6}>
              <Card size="small">
                <Statistic
                  title="总文章数"
                  value={pagination.total}
                  prefix={<FileTextOutlined />}
                  valueStyle={{ color: 'var(--fg)', fontSize: 18 }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6} md={6}>
              <Card size="small">
                <Statistic
                  title="已选择"
                  value={selectedRowKeys.length}
                  prefix={<SelectOutlined />}
                  valueStyle={{ color: 'var(--fg)', fontSize: 18 }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6} md={6}>
              <Card size="small">
                <Statistic
                  title="本周新增"
                  value={weeklyNewCount}
                  prefix={<CalendarOutlined />}
                  valueStyle={{ color: 'var(--fg)', fontSize: 18 }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6} md={6}>
              <Card size="small">
                <Statistic
                  title="总字数"
                  value={Math.round(articles.reduce((sum, article) => sum + article.word_count, 0) / 1000)}
                  suffix="K"
                  prefix={<EditOutlined />}
                  valueStyle={{ color: 'var(--fg)', fontSize: 18 }}
                />
              </Card>
            </Col>
          </Row>
        </div>

        {/* 筛选和批量操作工具栏 */}
        <Card style={{ marginBottom: 16 }}>
          <Row justify="space-between" align="middle" gutter={[8, 8]}>
            <Col xs={24} sm={24} md={14}>
              <Space size="small" wrap>
                <Select
                  placeholder="筛选状态"
                  style={{ width: 100 }}
                  allowClear
                  value={filters.is_read}
                  onChange={(value) => setFilters({ ...filters, is_read: value })}
                  size="small"
                >
                  <Option value={true}>已读</Option>
                  <Option value={false}>未读</Option>
                </Select>

                <Select
                  placeholder="精选状态"
                  style={{ width: 100 }}
                  allowClear
                  value={filters.is_featured}
                  onChange={(value) => setFilters({ ...filters, is_featured: value })}
                  size="small"
                >
                  <Option value={true}>精选</Option>
                  <Option value={false}>非精选</Option>
                </Select>

                <Select
                  placeholder="筛选标签"
                  style={{ width: 120 }}
                  allowClear
                  value={filters.tag_id}
                  onChange={(value) => setFilters({ ...filters, tag_id: value })}
                  size="small"
                >
                  {tags.map(tag => (
                    <Option key={tag.id} value={tag.id}>
                      <Tag color={tag.color}>{tag.name}</Tag>
                    </Option>
                  ))}
                </Select>

                <Select
                  placeholder="筛选文件夹"
                  style={{ width: 120 }}
                  allowClear
                  value={filters.folder_id}
                  onChange={(value) => setFilters({ ...filters, folder_id: value })}
                  size="small"
                >
                  {folders.map(folder => (
                    <Option key={folder.id} value={folder.id}>
                      <FolderOutlined /> {folder.name}
                    </Option>
                  ))}
                </Select>

                <Checkbox
                  checked={filters.is_archived}
                  onChange={(e) => setFilters({ ...filters, is_archived: e.target.checked })}
                >
                  显示已归档
                </Checkbox>
              </Space>
            </Col>

            <Col xs={24} sm={24} md={10}>
              <Space size="small" wrap>
                {selectedRowKeys.length > 0 && (
                  <>
                    <Text type="secondary">
                      已选择 <Text strong>{selectedRowKeys.length}</Text> 篇
                    </Text>

                    <Dropdown
                      menu={{ items: batchOperationItems }}
                      trigger={['click']}
                      disabled={selectedRowKeys.length === 0}
                    >
                      <Button type="primary" size="small">
                        批量操作
                      </Button>
                    </Dropdown>

                    <Button
                      icon={<ClearOutlined />}
                      onClick={() => setSelectedRowKeys([])}
                      size="small"
                    >
                      清除选择
                    </Button>
                  </>
                )}

                <Button
                  icon={<ExportOutlined />}
                  onClick={() => message.info('导出功能开发中')}
                  size="small"
                >
                  导出
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>

        {/* 文章列表表格 */}
        <Card>
          <Table
            rowSelection={handleRowSelection}
            columns={columns}
            dataSource={articles}
            rowKey="id"
            loading={loading}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
              pageSizeOptions: ['10', '20', '50', '100']
            }}
            onChange={handleTableChange}
            scroll={{ x: 1000 }}
            size="small"
            bordered={false}
          />
        </Card>

        {/* 批量操作模态框 */}
        <BatchOperationModal
          visible={batchModalVisible}
          onCancel={() => {
            setBatchModalVisible(false)
            setBatchOperation(null)
          }}
          onConfirm={handleBatchOperation}
          selectedCount={selectedRowKeys.length}
          operation={batchOperation}
          tags={tags}
          folders={folders}
        />
      </Content>
    </Layout>
  )
}

export default Articles
